import Heading, {
  HeadingSize,
  HeadingTag,
} from '@/components/ui/Heading/Heading';
import useLocale from '@/hooks/useLocale';
import { formatMoney } from '@/utils/helpers';
import { TypePlayKitPageTranslations } from '@/utils/translations/types/TypePlayKitPageTranslations';
import { useAccordion } from '@/utils/useAccordion';
import React from 'react';
import { TypeScheduledItemGroup } from '../../utils';
import interpolate from '@/utils/translations/interpolate';
import { useFlags } from 'launchdarkly-react-client-sdk';

type OrderDetailsProps = {
  t: TypePlayKitPageTranslations;
  group: Partial<TypeScheduledItemGroup> & {
    items: TypeScheduledItemGroup['items'];
  };
  noAccordion?: boolean;
};

function isNumeric(value: any) {
  return !isNaN(value);
}

const OrderDetails: React.FC<OrderDetailsProps> = ({
  t,
  group,
  noAccordion,
}) => {
  const { 'sub-squad-enable-tariff': subSquadEnableTariff } = useFlags();

  const { Icon, Accordion } = useAccordion({ defaultIsOpen: true });

  const locale = useLocale();

  const collapsedContent = (
    <div className="flex flex-row justify-between my-4">
      <Heading
        align="left"
        text={t.orderDetails}
        tag={HeadingTag.H3}
        size={HeadingSize.H3}
      />
      <Icon />
    </div>
  );

  const {
    subtotal_price,
    total_shipping,
    total_tax,
    total_price,
    currency_code,
    taxes_included,
    total_discounts,
    total_tariff = 0,
  } = group.order || {};

  const isTariffEnabled = subSquadEnableTariff && total_tariff > 0;

  const priceCopy =
    group.items.length > 1
      ? interpolate(t.totalBundlePrice, {
          bundleSize: group.items.length,
        })
      : t.price;

  const localizedSubTotal = subtotal_price ?? 0;
  // Handle potential null or undefined total price with a fallback
  const totalPrice = total_price ?? 0;
  // Subtract total tax if taxes are included, otherwise use the total price
  const priceWithoutTax = totalPrice - (total_tax ?? 0);
  const finalPrice = taxes_included ? priceWithoutTax : totalPrice;
  const expandedContent = (
    <div className="flex flex-col gap-0.5">
      {isNumeric(subtotal_price) && (
        <div className="flex flex-row justify-between">
          <Heading
            align="left"
            text={priceCopy}
            tag={HeadingTag.H4}
            size={HeadingSize.H4}
          />
          <p>{formatMoney(localizedSubTotal, locale, false)}</p>
        </div>
      )}
      {isNumeric(total_shipping) && (
        <div className="flex flex-row justify-between">
          <Heading
            align="left"
            text={t.shipping}
            tag={HeadingTag.H4}
            size={HeadingSize.H4}
          />
          {total_shipping ? (
            <p>{formatMoney(total_shipping, locale, false)}</p>
          ) : (
            <p className="uppercase">{t.free}</p>
          )}
        </div>
      )}
      {isNumeric(total_discounts) && (total_discounts ?? 0) > 0 && (
        <div
          className={`flex flex-row justify-between ${taxes_included ? 'border-b pb-3' : ''}`}
        >
          <Heading
            align="left"
            text={t.discount}
            tag={HeadingTag.H4}
            size={HeadingSize.H4}
          />
          <p className="w-full text-right">
            {formatMoney(-(total_discounts ?? 0), locale, false)}
          </p>
        </div>
      )}
      {isTariffEnabled && (
        <div
          className="flex flex-row justify-between"
          data-testid="order-details-tariff"
        >
          <Heading
            align="left"
            text={t.tariff}
            tag={HeadingTag.H4}
            size={HeadingSize.H4}
          />
          <p>{formatMoney(total_tariff ?? 0, locale, false)}</p>
        </div>
      )}
      {isNumeric(total_tax) && !taxes_included && (
        <div className="flex flex-row justify-between">
          <Heading
            align="left"
            text={t.tax}
            tag={HeadingTag.H4}
            size={HeadingSize.H4}
          />
          <p>{formatMoney(total_tax ?? 0, locale, false)}</p>
        </div>
      )}
      {isNumeric(total_price) && (
        <div className="flex flex-row justify-between border-t mt-3 pt-3 pb-6">
          <Heading
            align="left"
            text={t.total}
            tag={HeadingTag.H4}
            size={HeadingSize.H4}
          />
          <p className="uppercase text-base pr-2">{currency_code}</p>
          <p className="font-bold">
            {formatMoney(finalPrice ?? 0, locale, false)}
          </p>
        </div>
      )}
    </div>
  );

  if (noAccordion) {
    return (
      <div data-testid="no-accordion" className="flex flex-col gap-0.5">
        {expandedContent}
      </div>
    );
  }

  return (
    <Accordion
      collapsedContent={collapsedContent}
      expandedContent={expandedContent}
    />
  );
};

export default OrderDetails;
